// @file google map manager implementation

import type { Bounds, LatLngLiteral, MapOptions } from '@@/bits/map'
import { initMap, MapEventsManager, RELAXED_BOUND, RESTRICTED_BOUND } from '@@/bits/map'
import { MapManager } from '@@/bits/map_format/MapManager'
import type { MarkerColorsHash } from '@@/bits/map_theme'
import { getMapTypeIdFromThemeId, getMarkerColorsFromThemeId, getStylesFromThemeId } from '@@/bits/map_theme'

export class GoogleMapManager extends MapManager {
  initialize(latitude: number, longitude: number, zoom: number): void {
    this.map = initMap(this.rootEl, latitude, longitude, zoom, this.options)
    this.setupBoundsListener()
  }

  addClickListener(callback: (e: Event) => void): void {
    this.map?.addListener('click', callback)
  }

  setOptions(options: MapOptions): void {
    this.map?.setOptions(options)
  }

  getCenter(): LatLngLiteral {
    if (this.map == null) throw new Error('Map not initialized')
    return this.map.getCenter() as unknown as LatLngLiteral
  }

  getBounds(): Bounds | null {
    return this.map?.getBounds() ?? null
  }

  setZoom(zoom: number): void {
    this.map?.setZoom(zoom)
  }

  getZoom(): number {
    if (this.map == null) throw new Error('Map not initialized')
    return this.map.getZoom()
  }

  panTo(position: LatLngLiteral): void {
    this.map?.panTo(position)
  }

  setCenter(position: LatLngLiteral): void {
    this.map?.setCenter(position)
  }

  fitBounds(bounds: Bounds): void {
    this.map?.fitBounds(bounds)
  }

  setPopupMode(isPopupOpen: boolean): void {
    this.setOptions({
      restriction: isPopupOpen ? RELAXED_BOUND : RESTRICTED_BOUND,
    })
  }

  setTheme(themeId: number): void {
    this.setMapTheme(getStylesFromThemeId(themeId), getMapTypeIdFromThemeId(themeId))
    this.markerIcons = getMarkerColorsFromThemeId(themeId)
  }

  getMarkerIcons(): MarkerColorsHash | null {
    return this.markerIcons
  }

  addControl(control: HTMLElement, position: google.maps.ControlPosition): void {
    if (this.map == null) return
    this.map.controls[position].push(control)
  }

  addIdleListener(callback: () => void): void {
    if (this.map == null) return
    MapEventsManager.addListenerOnce(this.map, 'idle', callback)
  }

  public fromLatLngToPixel(latlng: LatLngLiteral): { x: number; y: number } | null {
    if (this.map == null) return null
    const projection = this.map.getProjection()
    if (projection == null) return null

    const point = projection.fromLatLngToPoint(new google.maps.LatLng(latlng))
    if (point == null) return null

    return { x: point.x, y: point.y }
  }

  public fromPixelToLatLng(pixel: { x: number; y: number }): LatLngLiteral | null {
    if (this.map == null) return null
    const projection = this.map.getProjection()
    if (projection == null) return null

    const latLng = projection.fromPointToLatLng(new google.maps.Point(pixel.x, pixel.y))
    if (latLng == null) return null

    return { lat: latLng.lat(), lng: latLng.lng() }
  }
}
